import { FC } from "react";
import { useSelector } from "react-redux";

import { Button } from "@livechat/design-system-react-components";

import { SubscriptionPhase } from "constants/subscription-phase";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { inTrial } from "helpers/subscription";
import { selectIsManagedByShopify, selectIsShopifyTrial, selectShopifyIntegration } from "store/entities/integrations";
import { selectManagedByPartner, selectManagedByPartnerEmail } from "store/entities/license";
import { selectCurrentSubscription, selectSubscriptionPhase } from "store/entities/subscriptions";

interface IProps {
  isSaving?: boolean;
  limitExceeded?: boolean;
  failed: boolean;
  inOnboarding?: boolean;
  ignoreLimit?: boolean;
  onSubmit?: () => void;
  label?: string;
  disabled?: boolean; // only respected in cases where inviting actually happens (not in Shopify, PP)
}

export const InviteButton: FC<IProps> = ({
  limitExceeded,
  isSaving,
  failed,
  inOnboarding,
  ignoreLimit,
  onSubmit,
  label,
  disabled,
}) => {
  const shopifyIntegration = useSelector(selectShopifyIntegration);
  const isHandledByShopify = useSelector(selectIsManagedByShopify);
  const isShopifyTrial = useSelector(selectIsShopifyTrial);
  const shopifyBillingUrl = shopifyIntegration?.settings?.billingUrl;
  const isHandledByPartner = useSelector(selectManagedByPartner);
  const subscription = useSelector(selectCurrentSubscription);
  const isTrial = inTrial(subscription, isShopifyTrial);
  const partnerEmail = useSelector(selectManagedByPartnerEmail);
  const isTrialCC = useSelector(selectSubscriptionPhase) === SubscriptionPhase.TrialCC;

  const handleContactPartner = (): void => {
    trackEngagement("Add Agents modal: Contact Partner clicked", EventPlace.Agents);
  };

  const handleContiuneOnShopify = (): void => {
    trackEngagement("Add Agents modal: Continue on Shopify clicked", EventPlace.Agents);
  };

  if (isHandledByPartner && (!isTrial || isTrialCC) && (limitExceeded || ignoreLimit))
    return (
      <Button
        kind="primary"
        onClick={handleContactPartner}
        href={`mailto:${partnerEmail}`}
        size={inOnboarding ? "large" : undefined}
      >
        Contact Partner
      </Button>
    );
  if (isHandledByShopify && (!isTrial || isTrialCC) && (limitExceeded || ignoreLimit))
    return (
      <Button
        kind="primary"
        onClick={handleContiuneOnShopify}
        href={shopifyBillingUrl}
        size={inOnboarding ? "large" : undefined}
      >
        Continue on Shopify
      </Button>
    );
  if (ignoreLimit)
    return (
      <Button
        kind="primary"
        onClick={onSubmit}
        loading={isSaving}
        disabled={failed || disabled}
        size={inOnboarding ? "large" : undefined}
        data-testid="upgrade-subscription-button"
      >
        Upgrade subscription
      </Button>
    );

  return (
    <Button
      kind="primary"
      type={onSubmit ? "button" : "submit"}
      data-testid="send-invitations-button"
      onClick={onSubmit ?? undefined}
      loading={isSaving}
      disabled={(failed && limitExceeded) || disabled}
      fullWidth={inOnboarding}
      size={inOnboarding ? "large" : undefined}
    >
      {label || "Send invitations"}
    </Button>
  );
};
